const { objectHasOwnPropertyOrSetter } = require('@/functions/objects');

/**
 * Do a basic hydration - just set all the values on the object where the keys match the data.
 * This can be used where a model has already been hydrated but then was converted to a standard object
 * (e.g. after being stored in IndexedDB)
 * now we need to get it back to the correct class.
 *
 * @param {Object} data
 * @param {Object|AbstractEntity} model
 */
const basicHydrate = (data, model) => {
    if (!data) {
        return model;
    }

    // We also want to cast related models to the appropriate types. For this we need to look at the hydration map.
    let hydrationMap = model.getHydrationMap ? model.getHydrationMap() : {};

    Object.keys(data).forEach((key) => {
        if (hydrationMap.hasOwnProperty(key) && hydrationMap[key].model && data[key]) {
            // Property value should be a single model.
            model[key] = basicHydrate(data[key], new hydrationMap[key].model());
        } else if (hydrationMap.hasOwnProperty(key) && hydrationMap[key].modelFunc && data[key]) {
            // Property value should be a single model.
            // The modelFunc function says what model that should be
            model[key] = basicHydrate(data[key], new (hydrationMap[key].modelFunc(data))());
        } else if (hydrationMap.hasOwnProperty(key) && hydrationMap[key].modelCollection) {
            // Property value should be an array of models.
            model[key] = basicHydrateArray(data[key], () => new hydrationMap[key].modelCollection());
        } else if (objectHasOwnPropertyOrSetter(model, key)) {
            // Property value should be exactly as passed in.
            model[key] = data[key];
        }
        // 'isHydrated' is a special key added to a model during hydration.
        // 'hasOwnProperty' will fail for this because it is not defined on every model, it is just added by
        // the hydrator. Set it to true if and only if it was present in the data and was truthy.
        if (key === 'isHydrated' && !!data.isHydrated) {
            model.isHydrated = true;
        }
    });

    return model;
};

/**
 * Run basicHydrate on an array of data.
 *
 * @param {array} data
 * @param {function} modelCallback
 *
 * @return {Array}
 */
const basicHydrateArray = (data, modelCallback) => {
    if (!data) {
        return [];
    }
    // let started = new Date();
    let results = data.map((datum) => basicHydrate(datum, modelCallback()));
    // console.debug(`Basic hydrated ${data.length} models in ${(new Date()).getTime() - started.getTime()}ms`);
    return results;
};

/**
 * Returns a function that can be called to hydrate an array of data into the model type specified.
 * Useful for a promise chain.
 *
 * @param {Function} Model
 *
 * @return {function(array): array}
 */
const createModelBasicHydrator = (Model) => (data) => basicHydrateArray(data, () => new Model());

module.exports = {
    basicHydrate,
    basicHydrateArray,
    createModelBasicHydrator,
};
